import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setSuccessMessage, setErrorMessage } from '../redux/actions/message';
import useWebSocketSync from './useWebSocketSync';
import { WEBSOCKET_CONFIG } from '../utils/websocketConstants';
import tokenService from '../services/token.service';

const STORAGE_KEY = 'activeSyncStates';

const useMultipleSyncProgress = (onSyncComplete, onSyncError) => {
  const dispatch = useDispatch();
  
  // Get auth token from localStorage (stable reference)
  const authToken = useMemo(() => tokenService.getLocalAccessToken(), []);
  
  // Store multiple sync states by boardId
  const [syncStates, setSyncStates] = useState({});
  // { 
  //   'boardId1': { isVisible, progress, message, status, boardId, boardName, jobId },
  //   'boardId2': { ... }
  // }

  // Initialize WebSocket hook
  const webSocketSync = useWebSocketSync(authToken);
  
  // Check if WebSocket is available and enabled
  const useWebSocketMode = WEBSOCKET_CONFIG.ENABLED && authToken && webSocketSync.connectionState.isConnected;

  // Track processed completion/error to avoid duplicate dispatches
  const processedJobsRef = useRef(new Set());
  
  // Track if initial restore has been done
  const hasRestoredRef = useRef(false);
  
  // Track restored states that need reconnection
  const pendingReconnectionsRef = useRef([]);

  // Load saved sync states from localStorage on mount
  useEffect(() => {
    if (hasRestoredRef.current) return;
    
    try {
      const savedStates = localStorage.getItem(STORAGE_KEY);
      if (savedStates) {
        const parsed = JSON.parse(savedStates);
        const now = Date.now();
        
        // Filter out completed/error/cancelled states and stale states (older than 1 hour)
        const activeStates = {};
        const reconnections = [];
        
        Object.entries(parsed).forEach(([boardId, state]) => {
          const isActive = state.status === 'running' || state.status === 'syncing' || state.status === 'waiting';
          const stateAge = now - (state.timestamp || 0);
          const isRecent = stateAge < 60 * 60 * 1000; // 1 hour
          
          if (isActive && isRecent && state.jobId) {
            activeStates[boardId] = {
              ...state,
              message: 'Reconnecting to sync...',
              isRestored: true
            };
            
            // Store for reconnection when WebSocket is ready
            reconnections.push({
              jobId: state.jobId,
              boardId: state.boardId,
              boardName: state.boardName
            });
          }
        });
        
        if (Object.keys(activeStates).length > 0) {
          setSyncStates(activeStates);
          pendingReconnectionsRef.current = reconnections;
        }
      }
    } catch (error) {
      console.error('Failed to restore sync states from localStorage:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
    
    hasRestoredRef.current = true;
  }, []);

  // Reconnect to WebSocket rooms when connection becomes ready
  useEffect(() => {
    if (!useWebSocketMode || !hasRestoredRef.current || pendingReconnectionsRef.current.length === 0) {
      return;
    }
    
    // Process all pending reconnections
    pendingReconnectionsRef.current.forEach(({ jobId, boardId, boardName }) => {
      webSocketSync.reconnectToRoom(jobId, boardId, boardName)
        .catch(error => {
          console.error(`Failed to reconnect to job ${jobId}:`, error);
        });
    });
    
    // Clear pending reconnections
    pendingReconnectionsRef.current = [];
  }, [useWebSocketMode, webSocketSync]);

  // Save sync states to localStorage whenever they change
  useEffect(() => {
    if (!hasRestoredRef.current) return; // Don't save during initial restore
    
    try {
      // Add timestamp to each state for staleness detection
      const statesToSave = {};
      Object.entries(syncStates).forEach(([boardId, state]) => {
        statesToSave[boardId] = {
          ...state,
          timestamp: state.timestamp || Date.now()
        };
      });
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(statesToSave));
    } catch (error) {
      console.error('Failed to save sync states to localStorage:', error);
    }
  }, [syncStates]);

  // Remove sync state
  const removeSyncState = useCallback((boardId) => {
    setSyncStates(prev => {
      const newStates = { ...prev };
      delete newStates[boardId];
      return newStates;
    });
  }, []);

  // Sync state synchronization with WebSocket
  useEffect(() => {
    if (!useWebSocketMode || !webSocketSync.syncStates) {
      return;
    }

    const wsSyncStates = webSocketSync.syncStates;
    
    // Batch all updates in a single setState call to avoid multiple re-renders
    setSyncStates(prev => {
      const newStates = { ...prev };
      let hasChanges = false;

      Object.keys(wsSyncStates).forEach(jobId => {
        const wsState = wsSyncStates[jobId];
        
        // Only update if we have a boardId
        if (wsState.boardId) {
          const boardId = wsState.boardId;
          const currentState = prev[boardId];
          
          // Check if state actually changed to avoid unnecessary updates
          const stateChanged = !currentState || 
            currentState.progress !== wsState.progress ||
            currentState.status !== wsState.status ||
            currentState.message !== wsState.message;
          
          if (stateChanged) {
            hasChanges = true;
            newStates[boardId] = {
              isVisible: wsState.status !== 'idle',
              progress: wsState.progress,
              message: wsState.message,
              status: wsState.status,
              boardId: wsState.boardId,
              boardName: wsState.boardName,
              jobId: wsState.jobId,
              waitInfo: wsState.waitInfo
            };
          }

          // Handle WebSocket completion - only once per job
          if (wsState.status === 'completed' && !processedJobsRef.current.has(jobId)) {
            processedJobsRef.current.add(jobId);
            dispatch(setSuccessMessage(`Project "${wsState.boardName}" synced successfully!`));
            
            // Call onSyncComplete callback if provided
            if (onSyncComplete && typeof onSyncComplete === 'function') {
              onSyncComplete(wsState.boardId, wsState.boardName);
            }
            
            setTimeout(() => {
              removeSyncState(wsState.boardId);
              webSocketSync.resetSync(jobId);
              processedJobsRef.current.delete(jobId); // Clean up tracking
            }, 5000);
          } else if (wsState.status === 'error' && !processedJobsRef.current.has(jobId)) {
            processedJobsRef.current.add(jobId);
            dispatch(setErrorMessage(wsState.error || 'Sync failed'));
            
            // Call onSyncError callback if provided
            if (onSyncError && typeof onSyncError === 'function') {
              onSyncError(wsState.boardId, wsState.boardName, wsState.error);
            }
            
            setTimeout(() => {
              processedJobsRef.current.delete(jobId); // Clean up tracking
            }, 5000);
          }
        }
      });

      // Only return new state if something actually changed
      return hasChanges ? newStates : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useWebSocketMode, webSocketSync.syncStates, dispatch, onSyncComplete, onSyncError]);

  // Start sync process
  const startSync = useCallback(async (boardId, boardName) => {
    try {
      // Add new sync state
      setSyncStates(prev => ({
        ...prev,
        [boardId]: {
          isVisible: true,
          progress: 0,
          message: 'Initializing sync...',
          status: 'running',
          boardId,
          boardName,
          jobId: null
        }
      }));

      // Try WebSocket first if available
      if (useWebSocketMode) {
        try {
          await webSocketSync.startSync(boardId, boardName);
          return { success: true, method: 'websocket' };
        } catch (wsError) {
          console.error('WebSocket sync failed:', wsError);
          throw wsError;
        }
      }

    } catch (error) {
      console.error('Sync error:', error);
      setSyncStates(prev => ({
        ...prev,
        [boardId]: {
          ...prev[boardId],
          status: 'error',
          message: error.message || 'Failed to start sync process',
          progress: 0
        }
      }));
      dispatch(setErrorMessage(error.message || 'Failed to sync project'));
      throw error;
    }
  }, [dispatch, useWebSocketMode, webSocketSync]);

  // Cancel sync
  const cancelSync = useCallback((boardId) => {
    const syncState = syncStates[boardId];
    
    // Cancel WebSocket sync if active
    if (useWebSocketMode && syncState && syncState.jobId) {
      webSocketSync.cancelSync(syncState.jobId);
    }
    
    setSyncStates(prev => ({
      ...prev,
      [boardId]: {
        ...prev[boardId],
        status: 'cancelled',
        message: 'Sync cancelled by user',
      }
    }));

    // Auto-remove after 3 seconds
    setTimeout(() => {
      removeSyncState(boardId);
    }, 3000);
  }, [useWebSocketMode, webSocketSync, removeSyncState, syncStates]);

  // Retry sync
  const retrySync = useCallback((boardId) => {
    const syncState = syncStates[boardId];
    if (syncState && syncState.boardName) {
      // Reset WebSocket state for this jobId if needed
      if (useWebSocketMode && syncState.jobId) {
        webSocketSync.resetSync(syncState.jobId);
      }
      startSync(boardId, syncState.boardName);
    }
  }, [syncStates, startSync, useWebSocketMode, webSocketSync]);

  // Hide specific progress bar
  const hideProgress = useCallback((boardId) => {
    removeSyncState(boardId);
  }, [removeSyncState]);

  // Get array of active sync states
  const activeSyncs = useMemo(() => {
    return Object.values(syncStates).filter(sync => sync.isVisible);
  }, [syncStates]);

  return {
    syncStates,
    activeSyncs,
    startSync,
    cancelSync,
    retrySync,
    hideProgress,
    // WebSocket status info
    isWebSocketEnabled: useWebSocketMode,
    webSocketConnectionState: webSocketSync.connectionState,
    // WebSocket actions
    retryWebSocketConnection: webSocketSync.retryConnection
  };
};

export default useMultipleSyncProgress;
